import React, { useEffect, useState, useCallback } from 'react';
import api, { API_BASE } from '@/lib/api';
import { useCycle } from '@/lib/cycle';
import { useAuth } from '@/lib/auth';
import { toast } from 'sonner';
import { PageHeader, Card, Loading, Empty, SectionBadge } from '@/components/UI';
import { Save, Send, Plus, Trash2 } from 'lucide-react';

export function useManualForm(section, defaultData) {
  const { currentCycle } = useCycle();
  const { user } = useAuth();
  const [data, setData] = useState(defaultData);
  const [status, setStatus] = useState('draft');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const refresh = useCallback(async () => {
    if (!currentCycle) return;
    setLoading(true);
    try {
      const r = await api.get(`/entries/${currentCycle.id}/${section}`);
      if (r.data) {
        setData(r.data.data || defaultData);
        setStatus(r.data.status || 'draft');
      } else {
        setData(defaultData);
        setStatus('draft');
      }
    } finally { setLoading(false); }
  // eslint-disable-next-line
  }, [currentCycle?.id, section]);

  useEffect(() => { refresh(); }, [refresh]);

  const save = async () => {
    if (!currentCycle) return;
    setSaving(true);
    try {
      await api.post(`/entries/${currentCycle.id}/${section}`, { data });
      toast.success('Draft saved');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  const submit = async () => {
    if (!currentCycle) return;
    setSubmitting(true);
    try {
      await api.post(`/entries/${currentCycle.id}/${section}`, { data });
      await api.post(`/entries/${currentCycle.id}/${section}/submit`);
      toast.success('Section submitted');
      setStatus('submitted');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Submit failed');
    } finally { setSubmitting(false); }
  };

  return { data, setData, status, loading, saving, submitting, save, submit, currentCycle, user };
}

export function FormShell({ title, subtitle, status, save, submit, saving, submitting, locked, canEdit, children }) {
  return (
    <div className="space-y-6">
      <PageHeader title={title} subtitle={subtitle} actions={
        <div className="flex items-center gap-2">
          <SectionBadge status={status} label={status.toUpperCase()} />
          {!locked && canEdit && (
            <>
              <button className="btn-secondary text-sm" onClick={save} disabled={saving || submitting} data-testid="btn-save-draft"><Save size={14} className="inline mr-1" />{saving ? 'Saving…' : 'Save Draft'}</button>
              <button className="btn-primary text-sm" onClick={submit} disabled={saving || submitting} data-testid="btn-submit-section"><Send size={14} className="inline mr-1" />{submitting ? 'Submitting…' : 'Submit'}</button>
            </>
          )}
        </div>
      } />
      {!canEdit && <div className="bg-amber-50 border border-amber-200 text-amber-800 rounded p-3 text-sm">You have read-only access to this section.</div>}
      {children}
    </div>
  );
}

export function NumberField({ label, value, onChange, suffix, ...props }) {
  return (
    <div>
      <label className="aampl-label">{label}</label>
      <div className="flex items-center gap-2">
        <input className="aampl-input" type="number" step="any" value={value ?? ''} onChange={(e) => onChange(e.target.value === '' ? '' : Number(e.target.value))} {...props} />
        {suffix && <span className="text-xs text-slate-500">{suffix}</span>}
      </div>
    </div>
  );
}

export function TextField({ label, value, onChange, ...props }) {
  return (
    <div>
      <label className="aampl-label">{label}</label>
      <input className="aampl-input" type="text" value={value ?? ''} onChange={(e) => onChange(e.target.value)} {...props} />
    </div>
  );
}

export function TextAreaField({ label, value, onChange, ...props }) {
  return (
    <div>
      <label className="aampl-label">{label}</label>
      <textarea className="aampl-input" rows={3} value={value ?? ''} onChange={(e) => onChange(e.target.value)} {...props} />
    </div>
  );
}

export function SelectField({ label, value, onChange, options, ...props }) {
  return (
    <div>
      <label className="aampl-label">{label}</label>
      <select className="aampl-input" value={value ?? ''} onChange={(e) => onChange(e.target.value)} {...props}>
        <option value="">Select…</option>
        {options.map((o) => typeof o === 'string' ? <option key={o} value={o}>{o}</option> : <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </div>
  );
}

export function CheckboxField({ label, value, onChange }) {
  return (
    <label className="flex items-center gap-2 cursor-pointer">
      <input type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)} />
      <span className="text-sm">{label}</span>
    </label>
  );
}

// Text input that shows Indian-grouped digits when blurred (12,34,567.89) and
// stores a raw Number. HTML type="number" can't display commas, so we manage
// focused/blurred states manually.
export function MoneyInput({ value, onChange, className = 'aampl-input', ...props }) {
  const [focused, setFocused] = useState(false);
  const [raw, setRaw] = useState('');
  const grouped =
    value === '' || value === null || value === undefined || Number.isNaN(Number(value))
      ? ''
      : Number(value).toLocaleString('en-IN', { maximumFractionDigits: 2 });
  return (
    <input
      {...props}
      className={className}
      type="text"
      inputMode="decimal"
      value={focused ? raw : grouped}
      onFocus={() => {
        setRaw(value === '' || value == null ? '' : String(value));
        setFocused(true);
      }}
      onBlur={() => setFocused(false)}
      onChange={(e) => {
        const cleaned = e.target.value.replace(/[^0-9.\-]/g, '');
        setRaw(cleaned);
        onChange(cleaned === '' ? '' : Number(cleaned));
      }}
    />
  );
}

export function MoneyField({ label, value, onChange, suffix, ...props }) {
  return (
    <div>
      <label className="aampl-label">{label}</label>
      <div className="flex items-center gap-2">
        <MoneyInput value={value} onChange={onChange} {...props} />
        {suffix && <span className="text-xs text-slate-500">{suffix}</span>}
      </div>
    </div>
  );
}

export function ReadOnlyField({ label, value, hint }) {
  return (
    <div>
      <label className="aampl-label">{label}</label>
      <div className="aampl-input bg-slate-50 cursor-default">{value == null || value === '' ? '—' : value}</div>
      {hint && <div className="text-[11px] text-slate-500 mt-1">{hint}</div>}
    </div>
  );
}

function FileCell({ value, onChange }) {
  const [busy, setBusy] = useState(false);
  const up = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setBusy(true);
    const fd = new FormData(); fd.append('file', f);
    try {
      const r = await api.post('/attachments', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      onChange({ filename: r.data.filename, original: r.data.original });
    } catch (e) { toast.error(e.response?.data?.detail || 'Upload failed'); }
    finally { setBusy(false); }
  };
  if (value?.filename) {
    return (
      <span className="text-xs whitespace-nowrap">
        <a href={`${API_BASE}/attachments/${value.filename}`} target="_blank" rel="noreferrer" className="text-blue-600 underline">{value.original || 'view'}</a>
        <button onClick={() => onChange(null)} className="text-rose-600 ml-1" title="Remove">×</button>
      </span>
    );
  }
  return <input type="file" accept=".pdf,image/*" disabled={busy} className="text-xs" onChange={up} />;
}

export function RepeatTable({ rows, columns, onChange, addLabel = 'Add Row', defaultRow }) {
  const update = (idx, key, val) => {
    const next = [...rows];
    next[idx] = { ...next[idx], [key]: val };
    onChange(next);
  };
  const remove = (idx) => {
    const next = rows.filter((_, i) => i !== idx);
    onChange(next);
  };
  const add = () => onChange([...rows, { ...defaultRow }]);
  return (
    <div>
      <div className="aampl-table-wrap">
        <table className="aampl-table">
          <thead><tr>{columns.map((c) => <th key={c.key} className={c.right ? 'text-right' : ''}>{c.label}</th>)}<th className="w-12"></th></tr></thead>
          <tbody>
            {rows.length === 0 ? <tr><td colSpan={columns.length + 1} className="text-center text-slate-500 py-4">No rows. Click "Add" below to insert.</td></tr> : rows.map((r, i) => (
              <tr key={i}>
                {columns.map((c) => (
                  <td key={c.key}>
                    {c.type === 'select' ? (
                      <select className="aampl-input" value={r[c.key] ?? ''} onChange={(e) => update(i, c.key, e.target.value)}>
                        <option value="">—</option>
                        {(c.options || []).map((o) => <option key={o.value || o} value={o.value || o}>{o.label || o}</option>)}
                      </select>
                    ) : c.type === 'number' ? (
                      <input className="aampl-input" type="number" step="any" value={r[c.key] ?? ''} onChange={(e) => update(i, c.key, e.target.value === '' ? '' : Number(e.target.value))} />
                    ) : c.type === 'date' ? (
                      <input className="aampl-input" type="date" value={r[c.key] ?? ''} onChange={(e) => update(i, c.key, e.target.value)} />
                    ) : c.type === 'textarea' ? (
                      <textarea className="aampl-input" rows={2} value={r[c.key] ?? ''} onChange={(e) => update(i, c.key, e.target.value)} />
                    ) : c.type === 'money' ? (
                      <MoneyInput value={r[c.key] ?? ''} onChange={(val) => update(i, c.key, val)} />
                    ) : c.type === 'checkbox' ? (
                      <input type="checkbox" checked={!!r[c.key]} onChange={(e) => update(i, c.key, e.target.checked)} />
                    ) : c.type === 'readonly' ? (
                      <span className="text-sm">{(r[c.key] === '' || r[c.key] == null) ? '—' : `${r[c.key]}${c.suffix || ''}`}</span>
                    ) : c.type === 'file' ? (
                      <FileCell value={r[c.key]} onChange={(v) => update(i, c.key, v)} />
                    ) : (
                      <input className="aampl-input" type="text" value={r[c.key] ?? ''} onChange={(e) => update(i, c.key, e.target.value)} />
                    )}
                  </td>
                ))}
                <td><button className="btn-ghost text-rose-600" onClick={() => remove(i)} title="Remove row"><Trash2 size={14} /></button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <button className="btn-ghost mt-2 text-sm" onClick={add}><Plus size={14} className="inline" /> {addLabel}</button>
    </div>
  );
}
